import React from 'react';
import { RightOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { RMCard } from '../RMCard';
import { Text, Tooltip } from '../../../components';
import { ENTITLEMENTS_TOOLTIP } from '../../../constants/tooltips';
import { LoadableValue, disabledWhileLoading, useCardLoadingState } from '../CardLoadingUtils';
import classNames from 'classnames';

import './ProjectDetailsCard.scss';

export const ProjectDetailsCard = ({
    projectName,
    currentWeek,
    totalWeeks,
    status,
    startDate,
    endDate,
    owner,
    costCenter,
    availableEntitlements,
    totalEntitlements,
    onManageClick,
    className = '',
    queryState,
    ...props
}) => {
    const { loading, isError, disabled } = useCardLoadingState(queryState);
    const manageLinkClassName = classNames('project-details-card-manage-link', { 'link-disabled': disabled });
    const statusClass = status === 'active' ? 'status-active' : status === 'inactive' ? 'status-inactive' : '';
    const statusLabel = status === 'active' ? 'In Progress' : status === 'inactive' ? 'Inactive' : status || '—';

    return (
        <RMCard className={classNames('project-details-card', className)} showMoreIcon={false} isError={isError} {...props}>
            <header className="project-details-card-header">
                <div className="header-left">
                    <Text size="large" color="secondary">
                        {projectName ? `${projectName} Details` : 'Project Details'}
                    </Text>
                    {onManageClick && (
                        <Text
                            color="primaryBlue"
                            bold
                            className={manageLinkClassName}
                            onClick={disabledWhileLoading(disabled, onManageClick)}
                        >
                            Manage
                            <RightOutlined />
                        </Text>
                    )}
                </div>
            </header>
            <div className="project-details-card-body">
                <div className="project-details-card-week-section">
                    <Text size="small" color="muted">
                        Week
                    </Text>
                    <LoadableValue isLoading={loading}>
                        {currentWeek != null ? (
                            <span className="project-details-card-week-value">
                                <Text size="medLarge" color="primaryBlue" extraBold>
                                    {currentWeek}
                                </Text>
                                {totalWeeks != null && (
                                    <Text size="medLarge" color="primaryBlue" extraBold>{` of ${totalWeeks}`}</Text>
                                )}
                            </span>
                        ) : (
                            <Text size="medLarge" color="primaryBlue" extraBold>
                                Dates needed
                            </Text>
                        )}
                    </LoadableValue>
                </div>
                <div className="project-details-card-fields">
                    <div className="project-details-card-field">
                        <Text size="small" color="muted">
                            Status
                        </Text>
                        <Text
                            size="small"
                            color="muted"
                            extraBold
                            className={classNames('project-details-card-status', statusClass)}
                        >
                            <LoadableValue isLoading={loading}>{statusLabel}</LoadableValue>
                        </Text>
                    </div>
                    <div className="project-details-card-field">
                        <Text size="small" color="muted">
                            Start Date
                        </Text>
                        <Text size="small" color="muted" extraBold>
                            <LoadableValue isLoading={loading}>{startDate || 'Not Set'}</LoadableValue>
                        </Text>
                    </div>
                    <div className="project-details-card-field">
                        <Text size="small" color="muted">
                            End Date
                        </Text>
                        <Text size="small" color="muted" extraBold>
                            <LoadableValue isLoading={loading}>{endDate || 'Not Set'}</LoadableValue>
                        </Text>
                    </div>
                    <div className="project-details-card-field">
                        <Text size="small" color="muted">
                            Owner
                        </Text>
                        <Text size="small" color="muted" extraBold>
                            <LoadableValue isLoading={loading}>{owner || 'Not Set'}</LoadableValue>
                        </Text>
                    </div>
                    <div className="project-details-card-field">
                        <Text size="small" color="muted">
                            Cost Center
                        </Text>
                        <Text size="small" color="muted" extraBold>
                            <LoadableValue isLoading={loading}>{costCenter || 'Not Set'}</LoadableValue>
                        </Text>
                    </div>
                    <div className="project-details-card-field">
                        <div className="entitlements-label">
                            <Text size="small" color="muted">
                                Entitlements
                            </Text>
                            <Tooltip
                                title={ENTITLEMENTS_TOOLTIP}
                                placement="right"
                                classNames={{ root: 'entitlements-tooltip' }}
                            >
                                <InfoCircleOutlined className="entitlements-icon" />
                            </Tooltip>
                        </div>
                        <Text size="small" color="muted" extraBold>
                            <LoadableValue isLoading={loading}>
                                {availableEntitlements != null && totalEntitlements != null ? (
                                    <>
                                        {availableEntitlements} <span className="entitlements-text">remaining out of</span>{' '}
                                        {totalEntitlements}
                                    </>
                                ) : (
                                    'Not Set'
                                )}
                            </LoadableValue>
                        </Text>
                    </div>
                </div>
            </div>
        </RMCard>
    );
};

ProjectDetailsCard.displayName = 'ProjectDetailsCard';
